// Google Ads specialist dashboard — API client. Thin wrappers over the shared
// api() helper; filters ride the query string (arrays comma-separated, the drill
// focus as flat `focus*` params) to match the server's googleAdsFiltersFromQuery
// parser and keep a dashboard URL shareable.

import { api } from '../../lib/api';
import type {
  GoogleAdsChannelBreakdownRow,
  GoogleAdsFilterOptions,
  GoogleAdsFilters,
  GoogleAdsSearchTermsResponse,
  GoogleAdsSummary,
  GoogleAdsTreeResponse,
} from '../../types/googleAdsReporting';

export function toQueryString(filters: GoogleAdsFilters): string {
  const p = new URLSearchParams();
  p.set('from', filters.from);
  p.set('to', filters.to);
  const add = (key: string, values?: string[]): void => {
    if (values && values.length > 0) p.set(key, values.join(','));
  };
  add('brandAccountIds', filters.brandAccountIds);
  add('groupAccountIds', filters.groupAccountIds);
  add('industries', filters.industries);
  add('accountManagerIds', filters.accountManagerIds);
  add('accountIds', filters.accountIds);
  add('countries', filters.countries);
  add('channelTypes', filters.channelTypes);
  add('campaignStatuses', filters.campaignStatuses);
  add('biddingStrategyTypes', filters.biddingStrategyTypes);
  add('currencies', filters.currencies);
  add('levels', filters.levels);
  // Per-level name tokens — comma-joined like every other array param.
  add('campaignName', filters.nameFilters?.campaignName);
  add('adGroupName', filters.nameFilters?.adGroupName);
  add('adName', filters.nameFilters?.adName);

  const focus = filters.focus;
  if (focus) {
    p.set('focusLevel', focus.level);
    p.set('focusAccountId', focus.accountId);
    if (focus.campaignId) p.set('focusCampaignId', focus.campaignId);
    if (focus.adGroupId) p.set('focusAdGroupId', focus.adGroupId);
    if (focus.adId) p.set('focusAdId', focus.adId);
  }
  return p.toString();
}

export function getGoogleAdsSummary(filters: GoogleAdsFilters): Promise<GoogleAdsSummary> {
  return api<GoogleAdsSummary>(`/api/google-ads/summary?${toQueryString(filters)}`);
}

export function getGoogleAdsChannelBreakdown(
  filters: GoogleAdsFilters,
): Promise<{ rows: GoogleAdsChannelBreakdownRow[] }> {
  return api<{ rows: GoogleAdsChannelBreakdownRow[] }>(
    `/api/google-ads/channel-breakdown?${toQueryString(filters)}`,
  );
}

export function getGoogleAdsTree(filters: GoogleAdsFilters): Promise<GoogleAdsTreeResponse> {
  return api<GoogleAdsTreeResponse>(`/api/google-ads/tree?${toQueryString(filters)}`);
}

export function getGoogleAdsSearchTerms(
  filters: GoogleAdsFilters,
): Promise<GoogleAdsSearchTermsResponse> {
  return api<GoogleAdsSearchTermsResponse>(`/api/google-ads/search-terms?${toQueryString(filters)}`);
}

export function getGoogleAdsFilterOptions(): Promise<GoogleAdsFilterOptions> {
  return api<GoogleAdsFilterOptions>('/api/google-ads/filter-options');
}
